import { Component, inject, OnDestroy, OnInit, ViewChild, DoCheck } from '@angular/core';
import { DynamicForm, FileRepresentation, SelectOption } from '../../../components/form-builder/form-builder.types';
import { MultiFormAccordionService } from '../../../components/multi-form-accordion/multi-form-accordion.service';
import { PpmRepository } from '../../../store/ppm-state/ppm-state.repository';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { PpmService } from '../ppm.service';
import { ButtonModule } from 'primeng/button';
import { PpmMilestonesService } from '../ppm-milestones.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { PpmWorkflowBlocksService, TaskVariant } from '../ppm-workflow-blocks.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { getPpmUpdatePlanFormObject } from './utils/ppm-edit.form-object';
import { BehaviorSubject, catchError, combineLatest, of } from 'rxjs';
import { url } from '@/utils/url.utils';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { ppmPreDraftSchema } from '@/validations/ppm/ppm-predraft.validation';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Ppm, PpmDetails } from '@/types';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { TasksStatus } from '@/types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getPpmDeleteObject } from './utils/ppm-delete-dialog-object';
import { SpinnerComponent } from '../../../components/shared/spinner/spinner.component';
import { CurrentTasksService } from '@/pages/current-tasks/current-tasks.service';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { AuthorizationService } from '@/services/authorization.service';
import { ppmMilestonesStoreFactory } from '@/store/ppm-state/milestones-state/milestones.store';
import { getPPMCommiteeDialog } from './utils/ppm-committee.dialog-object';
import { getPPMDownloadDialog } from './utils/ppm-download.dialog-object';
import { getPPMUploadDialog } from './utils/ppm-upload.dialog-object';
import { fileUtils } from '@/utils/file.utils';
import { getPpmConfirmWorkflowObject } from './utils/ppm-confirm-workflow.dialog.object';
import _ from 'lodash';
import { getPPMCommiteeMemberDialog } from './utils/ppm-committee-members.dialog-object';
import { getPPMCommitteeReviewDialog } from './utils/ppm-committee-review.dialog-object';
import { PpmMetaDataBlocksService } from '../ppm-metadata-blocks.service';
import { getPPMPVItems } from '../utils/ppm-pv-items';

@Component({
  selector: 'sm-ppm-new-plan',
  imports: [
    CommonModule,
    ButtonModule,
    PageHeaderComponent,
    FormBuilderComponent,
    BlockBuilderComponent,
    StatusComponent,
    TranslateModule,
    SpinnerComponent,
    DialogComponent,
  ],
  templateUrl: './edit-plan.component.html',
  styleUrl: './edit-plan.component.scss',
})
export class PpmEditPlanComponent implements OnInit, OnDestroy, DoCheck {
  //services
  ppmService = inject(PpmService);
  ppmMilestonesService = inject(PpmMilestonesService);
  ppmWorkflowBlocksService = inject(PpmWorkflowBlocksService);
  ppmMetadataBlocksService = inject(PpmMetaDataBlocksService);
  ppmRepository = inject(PpmRepository);

  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  committeeService = inject(CommitteeService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  fileService = inject(FileService);
  currentTasksService = inject(CurrentTasksService);
  authService = inject(AuthorizationService);

  showCommissionVpModal = false;

  id = this.activatedRoute.snapshot.params['id'] as number;

  //breadcrumb
  breadcrumb = [
    {
      label: 'ppm.title',
      routerLink: ['/ppm-current-plans'],
    },
    {
      label: 'ppm.edit',
      routerLink: [],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  ppmDialogObject?: DialogObject;

  loading$ = new BehaviorSubject<boolean>(true);
  isUpdatable$ = new BehaviorSubject<boolean>(true);

  specialCommitteeOptions: SelectOption[] = [];
  membersOptions: SelectOption[] = [];
  acOptions: SelectOption[] = [];
  formObject: DynamicForm = getPpmUpdatePlanFormObject({ store: this.ppmRepository, acOptions: this.acOptions });
  controlBlocks: DynamicBlocks[] = [];
  metadataBlocks: DynamicBlocks[] = [];
  ppmDetails: PpmDetails | null = null;
  ppm: Ppm | null = null;
  status = '';
  comment = new BehaviorSubject<string>('');
  commentError = new BehaviorSubject<string | null>(null);

  isAltered = new BehaviorSubject<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  ngOnInit() {
    this.loadData();
  }

  ngDoCheck() {
    const milestoneAltered = this.ppmMilestonesService.altered();

    const dto = this.ppmRepository.get('dto');
    const initialDto = this.ppmRepository.get('initialDto');
    const planAltered = !_.isEqual(dto, initialDto);

    this.isAltered.next(milestoneAltered || planAltered);
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.ppmRepository.reset();
  }

  onCommentChange(value: string) {
    this.comment.next(value);
    if (this.commentError.getValue()) {
      this.commentError.next(null);
    }
  }

  loadData() {
    this.loading$.next(true);
    this.multiFormAccordionService.formFactory = (id: string) => ppmMilestonesStoreFactory(id);
    this.ppmMilestonesService.initialize();
    //set ac options
    combineLatest({
      acWithNoPpm: this.ppmService.getAcWithNoPpm().pipe(catchError(() => of([]))),
      allAc: this.contractingAuthorityService.getCurrentUserContractingAuthority().pipe(catchError(() => of([]))),
      ppmDetails: this.ppmService.getPpmDetails(this.id),
      specialCommittees: this.committeeService.getAllSpecialCommittees(),
    }).subscribe(({ acWithNoPpm, allAc, ppmDetails, specialCommittees }) => {
      this.ppmDetails = ppmDetails;

      this.specialCommitteeOptions = specialCommittees.map(committee => {
        return { name: committee.nom, code: committee.id };
      });

      if (this.ppmDetails?.comId) {
        this.committeeService.getComMembers(this.ppmDetails?.comId).subscribe(comMembers => {
          this.membersOptions = comMembers.map(member => {
            return { name: `${member.firstName} ${member.lastName}`, code: member.id };
          });
        });
      }
      this.multiFormAccordionService.formFactory = (id: string) => ppmMilestonesStoreFactory(id);
      this.ppmMilestonesService.setMilestoneDtos(ppmDetails.ppm.realisations || []);
      this.ppmRepository.setResponseAndDto(ppmDetails.ppm);
      this.breadcrumb[1].label = `Plan Actual - ${ppmDetails.ppm.ref}`;
      this.status = ppmDetails.ppm.status;

      this.ppm = ppmDetails.ppm;
      if (this.authService.isRedacteur() && (TasksStatus.BROUILLON == this.status || ppmDetails.taskName === TaskVariant.RECTIFICATION))
        this.isUpdatable$.next(true);
      else this.isUpdatable$.next(false);

      this.metadataBlocks = this.ppmMetadataBlocksService.getMetadataBlocks({
        pvItems: getPPMPVItems(ppmDetails.ppm),
        ppmOpinions: ppmDetails.ppm.avis,
      });

      if (ppmDetails.ppm.status !== TasksStatus.PUBLIEE)
        this.controlBlocks =
          !ppmDetails.ppm.processInstanceId && this.status === TasksStatus.BROUILLON
            ? this.ppmWorkflowBlocksService.getDraftPpmBlocks({
                refreshCount: ppmDetails.ppm.nbrActualisation,
                save: () => {
                  this.updatePpm('draft');
                },
                rest: () => this.reset(),
                validate: () => {
                  if (this.isAltered.getValue()) {
                    this.ppmDialogObject = getPpmConfirmWorkflowObject({
                      action: () => {
                        this.updatePpm('draft', () => this.startWorkflow());
                      },
                      toggleVisiblity: () => this.dialogComponent.toggleVisible(),
                    });
                    this.dialogComponent.toggleVisible();
                  } else {
                    this.startWorkflow();
                  }
                },
                isAltered: this.isAltered,
                accessHistory: () => this.router.navigate(['/ppm-version-history', ppmDetails.ppm.id]),
              })
            : this.ppmWorkflowBlocksService.getFlowablePpmBlocks({
                refreshCount: ppmDetails.ppm.nbrActualisation,
                task: ppmDetails,
                save: () => this.updatePpm('flowable'),
                rest: () => this.reset(),
                remove: () => {
                  this.ppmDialogObject = getPpmDeleteObject({
                    store: this.ppmRepository,
                    action: () => this.removePpm(),
                    toggleVisiblity: () => this.dialogComponent.toggleVisible(),
                  });
                  this.dialogComponent.toggleVisible();
                },
                validate: (value?: string) => this.validateByDialog(ppmDetails, value),
                isAltered: this.isAltered,
                accessHistory: () => this.router.navigate(['/ppm-version-history', ppmDetails.ppm.id]),
                openDownloadDialog: () => this.validateAcStep('download'),
                openUploadDialog: () => this.validateAcStep('upload'),
                reject: () => this.nextStep(ppmDetails.taskId, 'false'),
                comment: this.comment,
                onCommentChange: (value: string) => this.onCommentChange(value),
                error: this.commentError,
              });

      const selectedAc = allAc.find(ac => ac.id === this.ppmRepository.getNested('dto', 'acId'));
      this.acOptions = [{ name: selectedAc?.nom, code: selectedAc?.id }];

      this.acOptions = this.acOptions.concat(
        acWithNoPpm.map(ac => {
          return { name: ac.acName, code: ac.acId };
        })
      );

      this.formObject = getPpmUpdatePlanFormObject({
        store: this.ppmRepository,
        acOptions: this.acOptions,
        isUpdatable: this.isUpdatable$,
        download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.PPM),
        openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.PPM),
        loading: this.loading$,
      });
      this.loading$.next(false);
    });
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.ppmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.ppmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.PPM_MODEL
    );
  }

  nextStep(taskId?: string, value?: string) {
    const isUpdateMode = this.isUpdatable$.getValue();

    const formData = isUpdateMode
      ? this.ppmRepository.getUpdatePayload(this.ppmMilestonesService.getAllMilestoneDtos(), 'ppm.').formData
      : new FormData();

    const commentValue = this.comment.getValue();
    if (value === 'false' && !commentValue) {
      this.commentError.next('Veuillez saisir un commentaire');
      return;
    }

    if (value != undefined) formData.append('validate', value.toString());
    if (this.ppmRepository.getNested('dto', 'comId')) formData.append('comId', this.ppmRepository.getNested('dto', 'comId'));
    if (this.ppmRepository.getNested('dto', 'assigneeUserId'))
      formData.append('assigneeUserId', this.ppmRepository.getNested('dto', 'assigneeUserId'));
    formData.append('comment', commentValue);

    let isPayloadValid = true;
    let milestoneValid = true;

    if (isUpdateMode) {
      const actualizedCount = this.ppmRepository.getNested<number>('response', 'nbrActualisation');
      const year = this.ppmRepository.getNested('dto', 'annee');
      milestoneValid = this.ppmMilestonesService.validateAllMilestoneDtos(
        'flowable',
        year ? new Date(Number(year), 0, 1) : undefined,
        actualizedCount
      );
      const payload = this.ppmRepository.getCreatePayload(this.ppmMilestonesService.getAllMilestoneDtos());
      const result = ppmPreDraftSchema.safeParse(payload.data);

      isPayloadValid = result.success;

      if (!isPayloadValid && result.error) {
        this.ppmRepository.setErrors(result.error.flatten().fieldErrors);
        return;
      }
    }
    if (isPayloadValid && milestoneValid) {
      this.loading$.next(true);
      this.currentTasksService.validatePpm(Number(taskId), formData).subscribe({
        next: () => {
          this.comment.next('');
          this.multiFormAccordionService.reset(0);
          this.ppmRepository.reset();
          url.reloadComponent(this.router);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: "Le ppm a été passé pour l'étape suivante",
          });
          this.loading$.next(false);
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || "Une erreur est survenue lors de la passage du PPM  l'étape suivante",
          });
          this.loading$.next(false);
        },
      });
    }
  }

  validateByDialog(ppmDetails: PpmDetails | null, value?: string) {
    if (!ppmDetails) return;
    if (ppmDetails.taskName == TaskVariant.AFFECTATION_COMMISSION_PERMANENTE) {
      this.ppmDialogObject = getPPMCommiteeDialog({
        store: this.ppmRepository,
        validate: () => this.nextStep(ppmDetails.taskId, value),
        committees: this.specialCommitteeOptions,
      });
      this.dialogComponent.toggleVisible();
    } else if (ppmDetails.taskName == TaskVariant.AFFECTATION_COMMISSION_SPECIALISEE) {
      this.ppmDialogObject = getPPMCommiteeMemberDialog({
        store: this.ppmRepository,
        validate: () => this.nextStep(ppmDetails.taskId, value),
        members: this.membersOptions,
      });
      this.dialogComponent.toggleVisible();
    } else if (ppmDetails.taskName == TaskVariant.AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE) {
      this.ppmDialogObject = getPPMCommitteeReviewDialog({
        store: this.ppmRepository,
        validate: () => this.validateReporterStep(ppmDetails.taskId),
      });
      this.dialogComponent.toggleVisible();
    } else {
      this.nextStep(ppmDetails?.taskId, value);
    }
  }

  validateAcStep(variant: 'download' | 'upload') {
    if (variant === 'download')
      this.ppmDialogObject = getPPMDownloadDialog({
        store: this.ppmRepository,
        validate: () => {
          this.ppmService
            .downloadPpmModel({
              ppmId: this.ppmRepository.getNested('response', 'id'),
              ppmVer: this.ppmRepository.getNested('response', 'version'),
              signataire: this.ppmRepository.getNested('download', 'signPerson'),
              fonction: this.ppmRepository.getNested('download', 'function'),
            })
            .subscribe((blob: Blob) => {
              fileUtils.triggerBlobDownload(blob, `${this.ppmDetails?.ppm.ref}_modele.pdf`);
            });
          this.dialogComponent.toggleVisible();
        },
      });
    else
      this.ppmDialogObject = getPPMUploadDialog({
        store: this.ppmRepository,
        validate: () => {
          const modelPpmFileAsArray = this.ppmRepository.getNested<File[]>('upload', 'modelPpmFile');
          if (modelPpmFileAsArray.length === 0) {
            this.messageService.add({
              severity: 'error',
              summary: 'Fichier manquant',
              detail: 'Veuillez sélectionner un fichier avant de passer pour validation',
            });
            return;
          }
          const formData = new FormData();
          const modelPpmFile = modelPpmFileAsArray[0];
          formData.append('validate', 'true');
          formData.append('comment', this.comment.getValue());
          formData.append('modelPpmFile', modelPpmFile);
          if (this.ppmDetails?.taskId) {
            this.ppmService.uploadPpmModel(this.ppmDetails?.taskId, formData).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Succès',
                  detail: 'Le PPM a été passé pour validation avec succès',
                });
                this.dialogComponent.toggleVisible();
                url.reloadComponent(this.router);
              },
              error: err => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: err.error.message || "Une erreur est survenue lors de la passage du PPM  l'étape suivante",
                });
              },
            });
          }
        },
      });
    this.dialogComponent.toggleVisible();
  }

  validateReporterStep(taskId: string) {
    const formData = new FormData();
    formData.append('avis', this.ppmRepository.getNested<string>('review', 'comment'));
    const file = this.ppmRepository.getNested<File[]>('review', 'files')[0];
    if (file) formData.append('noteFile', file);

    this.ppmService.validateReporterReview(taskId, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'L’avis du rapporteur a été soumis avec succès',
        });
        this.dialogComponent.toggleVisible();
        url.reloadComponent(this.router);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || "Une erreur est survenue lors de la soumission de l'avis du rapporteur",
        });
      },
    });
  }

  startWorkflow() {
    this.ppmService.startWorkflow(this.ppmRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.multiFormAccordionService.reset(0);
        this.ppmRepository.reset();
        url.reloadComponent(this.router);
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le Plan de passation des marchés a été passé pour validation avec succès',
        });
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message,
        });
      },
    });
  }

  updatePpm(mode: 'draft' | 'flowable', onSuccess?: () => void) {
    const actualizedCount = this.ppmRepository.getNested<number>('response', 'nbrActualisation');
    const year = this.ppmRepository.getNested('dto', 'annee');
    const milestoneValidation = this.ppmMilestonesService.validateAllMilestoneDtos(
      mode,
      year ? new Date(Number(year), 0, 1) : undefined,
      actualizedCount
    );
    const payload = this.ppmRepository.getUpdatePayload(this.ppmMilestonesService.getAllMilestoneDtos());
    const result = ppmPreDraftSchema.safeParse(payload.data);

    if (result.success && milestoneValidation) {
      this.loading$.next(true);
      this.ppmService
        .updatePpm(this.ppmRepository.getNested('response', 'id'), this.ppmRepository.getNested('response', 'version'), payload.formData)
        .subscribe({
          next: () => {
            if (onSuccess) onSuccess();
            this.multiFormAccordionService.reset(0);
            this.ppmRepository.reset();
            url.reloadComponent(this.router);
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: 'Le PPM a été mis à jour avec succès',
            });
            this.loading$.next(false);
          },
          error: err => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: err.error.message || 'Une erreur est survenue lors de la mise à jour du ppm',
            });
            this.loading$.next(false);
          },
        });
    } else if (result.error) {
      this.ppmRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  removePpm() {
    this.ppmService.deletePpm(this.ppmRepository.getNested('response', 'id'), this.ppmRepository.getNested('response', 'version')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le PPM a été supprimé avec succès',
        });
        this.dialogComponent.toggleVisible();
        this.ppmRepository.reset();
        url.reloadComponent(this.router);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || 'Une erreur est survenue lors de la suppression du ppm',
        });
        this.dialogComponent.toggleVisible();
      },
    });
  }

  reset() {
    this.ppmRepository.reset();
    this.multiFormAccordionService.reset(0);
    if (this.acOptions.length === 1) {
      this.ppmRepository.setNested('dto', 'acId', this.acOptions[0].code);
      this.ppmRepository.set('acsSelected', this.acOptions[0]);
    }
  }
}
